import { useState } from "react";
import { IconAlertCircle, IconLockFilled, IconX } from "@tabler/icons-react";
import { Alert, Button, Card, Divider, Group, Input, NumberInput, Stack, Switch, Text, Textarea, Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui";
import {
  CONFIG_POR_DEFECTO,
  ConfiguracionDocumentalFields,
  configuracionValida,
  type ConfiguracionDocumental,
} from "@/features/admin-gestion-documental/configuracion-documental";
import type { CategoriaDocumentoDto } from "../services/categorias-documento-service";
import { useCrearCategoria, useEditarCategoria } from "../hooks/use-categoria-documento-mutations";

/**
 * Form de creación/edición de categoría (Gestión Documental · fase 2 · catálogo CREABLE). El
 * código no cambia una vez creada (lo guardan los documentos). Las de sistema no se eliminan ni
 * desactivan; las que registra el servidor (factura del CFDI / Invoice) tienen extensiones fijas.
 */
export function CategoriaDocumentoForm({
  categoria,
  onSalir,
}: {
  categoria?: CategoriaDocumentoDto;
  onSalir: () => void;
}) {
  const esEdicion = !!categoria;
  const esSistema = categoria?.esSistema ?? false;
  const esDelServidor = categoria?.esDelServidor ?? false;

  const [codigo, setCodigo] = useState(categoria?.codigo ?? "");
  const [nombre, setNombre] = useState(categoria?.nombre ?? "");
  const [descripcion, setDescripcion] = useState(categoria?.descripcion ?? "");
  const [extensiones, setExtensiones] = useState<string[]>(categoria?.extensiones ?? []);
  const [tamanoMB, setTamanoMB] = useState<number | null>(
    categoria?.tamanoMaximoBytes ? Math.round(categoria.tamanoMaximoBytes / 1024 / 1024) : null
  );
  const [orden, setOrden] = useState<number>(categoria?.orden ?? 99);
  const [requiereTipo, setRequiereTipo] = useState(categoria?.requiereTipo ?? false);
  const [configuracion, setConfiguracion] = useState<ConfiguracionDocumental>(categoria?.configuracion ?? CONFIG_POR_DEFECTO);

  const crear = useCrearCategoria({ onCreada: onSalir });
  const editar = useEditarCategoria({ onEditada: onSalir });
  const guardando = crear.isPending || editar.isPending;

  const errorConfig = configuracionValida(configuracion, true);
  const codigoValido = /^[A-Za-z][A-Za-z0-9]{1,39}$/.test(codigo.trim());
  const inputValido =
    nombre.trim().length > 0 && extensiones.length > 0 && (tamanoMB === null || tamanoMB > 0) && (esEdicion || codigoValido) && !errorConfig;

  const handleGuardar = () => {
    const input = {
      nombre: nombre.trim(),
      descripcion: descripcion.trim() || null,
      extensiones,
      tamanoMaximoBytes: tamanoMB ? tamanoMB * 1024 * 1024 : null,
      orden,
      requiereTipo,
      configuracion,
    };
    if (esEdicion) editar.mutate({ id: categoria!.id, input });
    else crear.mutate({ codigo: codigo.trim(), ...input });
  };

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Text fw={600}>{esEdicion ? "Editar categoría" : "Nueva categoría"}</Text>

        {esSistema ? (
          <Alert variant="readonly" icon={<IconLockFilled size={14} />}>
            {esDelServidor
              ? "Categoría de sistema · la registra el servidor al cargar la factura: el código y las extensiones no cambian. Puedes editar el nombre, la descripción y la configuración documental."
              : "Categoría de sistema · el código no cambia y no se puede eliminar ni desactivar. Puedes editar todo lo demás."}
          </Alert>
        ) : null}

        {!esEdicion ? (
          <Alert variant="info" icon={<IconAlertCircle size={14} />}>
            La categoría nueva se usa en cuanto la agregues a los <strong>requisitos documentales</strong> de un tipo y
            subtipo de solicitud: solo se pueden subir los documentos configurados ahí.
          </Alert>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Input
                  label="Código"
                  placeholder="ActaConstitutiva"
                  value={codigo}
                  onChange={(e) => setCodigo(e.currentTarget.value)}
                  disabled={esEdicion}
                  required
                  maxLength={40}
                  error={!esEdicion && codigo.length > 0 && !codigoValido ? "Empieza con letra; solo letras y números, sin espacios." : undefined}
                  description={esEdicion ? "No editable · lo guardan los documentos" : "Identificador único · solo letras y números"}
                />
              </span>
            </TooltipTrigger>
            {esEdicion && <TooltipContent>El código identifica la categoría en los documentos ya subidos</TooltipContent>}
          </Tooltip>
          <Input
            label="Nombre visible"
            placeholder="Acta constitutiva"
            value={nombre}
            onChange={(e) => setNombre(e.currentTarget.value)}
            required
            maxLength={120}
            description="Lo que ve el usuario en la UI"
          />
        </div>

        <Textarea
          label="Descripción"
          placeholder="Ayuda al usuario a saber qué subir"
          value={descripcion}
          onChange={(e) => setDescripcion(e.currentTarget.value)}
          maxLength={400}
          rows={2}
        />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {esDelServidor ? (
            <Input label="Extensiones permitidas" value={extensiones.join(", ")} disabled description="Fijas · las registra el servidor" />
          ) : (
            <ExtensionesInput value={extensiones} onChange={setExtensiones} label="Extensiones permitidas" description="Una por una · agrega el punto" />
          )}
          <NumberInput
            label="Tamaño máximo (MB)"
            value={tamanoMB ?? undefined}
            onChange={(v) => setTamanoMB(typeof v === "number" && v > 0 ? v : null)}
            min={1}
            max={500}
            step={1}
            placeholder="Límite global"
            description="Vacío = el límite global de documentos"
          />
          <NumberInput label="Orden" value={orden} onChange={(v) => setOrden(typeof v === "number" ? v : 99)} min={0} max={9999} step={1} description="Orden visual en la lista" />
        </div>

        {!esDelServidor && (
          <Switch
            label="Exige tipo de documento"
            description="Al subir, el solicitante clasifica cada archivo con un tipo del catálogo de esta categoría."
            checked={requiereTipo}
            onCheckedChange={setRequiereTipo}
          />
        )}

        <Divider />
        <ConfiguracionDocumentalFields value={configuracion} onChange={setConfiguracion} />
        {errorConfig && (
          <Text size="xs" c="peligro-600">
            {errorConfig}
          </Text>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onSalir} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} loading={guardando} disabled={!inputValido}>
            {esEdicion ? "Guardar cambios" : "Crear categoría"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

/**
 * Reemplazo puntual de `<TagsInput>` de Mantine · no hay otro call-site en
 * el repo que lo necesite, así que vive local a este form en vez de ser un
 * átomo compartido. Enter/coma agrega, Backspace en vacío quita el último.
 */
function ExtensionesInput({
  value,
  onChange,
  label,
  description,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  label: string;
  description?: string;
}) {
  const [draft, setDraft] = useState("");

  const agregar = () => {
    const v = draft.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setDraft("");
  };

  return (
    <div className="flex flex-col gap-1">
      <label className="text-sm font-medium text-foreground">
        {label}
        <span className="text-peligro-600"> *</span>
      </label>
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      <div className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background p-1.5 focus-within:border-multiva-600 focus-within:ring-2 focus-within:ring-multiva-600/30">
        {value.map((ext) => (
          <span key={ext} className="inline-flex items-center gap-1 rounded-sm bg-muted px-1.5 py-0.5 text-xs">
            {ext}
            <button type="button" onClick={() => onChange(value.filter((e) => e !== ext))} className="text-muted-foreground hover:text-foreground">
              <IconX size={10} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              agregar();
            } else if (e.key === "Backspace" && draft === "" && value.length > 0) {
              onChange(value.slice(0, -1));
            }
          }}
          onBlur={agregar}
          placeholder=".pdf, .xml, .docx…"
          className="min-w-24 flex-1 border-none bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
      </div>
    </div>
  );
}
